import { useEffect, useRef, useState, ChangeEvent } from "react";
import { PostFileIn } from "../api/posts";
import { useS3Upload } from "../hooks/useS3Upload";

const ALLOWED_MIME = /^(image\/(jpeg|png|webp)|application\/pdf)$/;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
export const MAX_FILES = 8;

interface FilesFieldProps {
  files: PostFileIn[];
  onChange: (files: PostFileIn[]) => void;
  /** Вызывается когда родитель реально трогал набор файлов (для filesChanged на странице правки). */
  onTouched?: () => void;
  /** Сообщить об ошибке наверх (страница показывает единый ErrorBanner). */
  onError: (msg: string) => void;
  /** Проброс состояния загрузки — чтобы страница заблокировала «Отправить» на время аплоада. */
  onUploadingChange?: (uploading: boolean) => void;
  /** s3_key → download URL для уже загруженных файлов (страница редактирования). */
  existingPreviews?: Record<string, string>;
}

function isImage(mime: string): boolean {
  return mime.startsWith("image/");
}

/**
 * Поле загрузки фото/PDF для достижения.
 *
 * Поведение, важное для родителя на телефоне:
 * - `multiple` + последовательная загрузка — можно выбрать сразу несколько фото
 *   (например все страницы диплома) одним касанием, а не по одному.
 * - миниатюры-превью выбранных снимков (object URL для новых, presigned URL для
 *   уже загруженных на странице правки) — видно ЧТО прикрепил, а не только имя файла.
 * - крупная плитка «＋ Фото / PDF» вместо мелкого системного инпута.
 */
export function FilesField({
  files,
  onChange,
  onTouched,
  onError,
  onUploadingChange,
  existingPreviews,
}: FilesFieldProps) {
  const { uploadFile, uploading } = useS3Upload();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  // s3_key → object URL для только что добавленных картинок.
  const [localPreviews, setLocalPreviews] = useState<Record<string, string>>({});
  // Все созданные object URL — чтобы отозвать при размонтировании (без утечек памяти).
  const createdUrls = useRef<string[]>([]);
  useEffect(
    () => () => {
      createdUrls.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // позволяет выбрать тот же файл повторно
    if (picked.length === 0) return;
    onError("");

    let current = [...files];
    const freshPreviews: Record<string, string> = {};
    const skipped: string[] = [];

    for (const file of picked) {
      if (current.length >= MAX_FILES) {
        skipped.push(`${file.name} — лимит ${MAX_FILES} файлов`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        skipped.push(`${file.name} — больше 10 МБ`);
        continue;
      }
      if (!ALLOWED_MIME.test(file.type)) {
        skipped.push(`${file.name} — только JPEG, PNG, WebP, PDF`);
        continue;
      }
      try {
        const uploaded = await uploadFile(file);
        uploaded.sort_order = current.length;
        current = [...current, uploaded];
        if (isImage(file.type)) {
          const url = URL.createObjectURL(file);
          createdUrls.current.push(url);
          freshPreviews[uploaded.s3_key] = url;
        }
      } catch {
        skipped.push(`${file.name} — ошибка загрузки`);
      }
    }

    if (Object.keys(freshPreviews).length) {
      setLocalPreviews((p) => ({ ...p, ...freshPreviews }));
    }
    if (current.length !== files.length) {
      onChange(current);
      onTouched?.();
    }
    if (skipped.length) {
      onError(`Не добавлено: ${skipped.join("; ")}`);
    }
  }

  function remove(idx: number) {
    const f = files[idx];
    const url = localPreviews[f.s3_key];
    if (url) {
      URL.revokeObjectURL(url);
      setLocalPreviews((p) => {
        const next = { ...p };
        delete next[f.s3_key];
        return next;
      });
    }
    onChange(files.filter((_, i) => i !== idx).map((x, i) => ({ ...x, sort_order: i })));
    onTouched?.();
  }

  function previewFor(f: PostFileIn): string | null {
    return localPreviews[f.s3_key] ?? existingPreviews?.[f.s3_key] ?? null;
  }

  const full = files.length >= MAX_FILES;

  return (
    <div className="mb-4">
      <label className="block mb-2 text-sm font-medium">
        Фото или скан{" "}
        <span className="text-[var(--color-muted)] font-normal">
          ({files.length}/{MAX_FILES})
        </span>
      </label>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {files.map((f, i) => {
          const url = previewFor(f);
          return (
            <div
              key={f.s3_key}
              className="relative aspect-square border border-[var(--color-line)] bg-[var(--color-paper)] overflow-hidden"
            >
              {url ? (
                <img
                  src={url}
                  alt={f.original_filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-1.5">
                  <span className="text-2xl">📄</span>
                  <span className="text-[10px] leading-tight text-[var(--color-muted)] truncate w-full mt-1">
                    {f.original_filename}
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Убрать ${f.original_filename}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs grid place-items-center hover:bg-black/80 transition-colors"
              >
                ✕
              </button>
            </div>
          );
        })}

        {!full && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-[var(--color-line)] hover:border-[var(--color-accent-2)] flex flex-col items-center justify-center text-[var(--color-muted)] disabled:opacity-50 transition-colors"
          >
            {uploading ? (
              <span className="text-xs">Загрузка…</span>
            ) : (
              <>
                <span className="text-3xl leading-none">＋</span>
                <span className="text-[11px] mt-1">Фото / PDF</span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={onPick}
        className="hidden"
      />

      <p className="mt-2 text-xs text-[var(--color-muted)]">
        JPEG, PNG, WebP или PDF. До {MAX_FILES} файлов, каждый до 10 МБ. Можно
        выбрать сразу несколько.
      </p>
    </div>
  );
}
